import { Link } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import AnimatedSection from "@/components/base/AnimatedSection";
import { categories } from "@/mocks/categories";
import { features } from "@/mocks/features";
import { comparisonData } from "@/mocks/comparison";
import { steps } from "@/mocks/steps";
import { articles } from "@/mocks/articles";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://oopbuyspreadsheets.beauty";

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "name": "Oopbuy Spreadsheet Guide",
      "url": SITE_URL,
      "description": "Complete Oopbuy Spreadsheet guide covering tutorials, comparisons, safety tips and step-by-step instructions. Learn how to use Oopbuy Spreadsheet for organized shopping, faster ordering and better product discovery.",
      "inLanguage": "en-US",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${SITE_URL}/search?q={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    },
    {
      "@type": "WebPage",
      "name": "Oopbuy Spreadsheet Guide – Learn, Compare & Shop Smarter",
      "url": SITE_URL,
      "description": "A complete hub for understanding Oopbuy Spreadsheet systems — tutorials, comparisons, and safety insights all in one structured resource. Beginner-friendly explanations, step-by-step tutorials, and real comparison tables.",
      "isPartOf": { "@id": `${SITE_URL}/#website` },
      "about": { "@type": "Thing", "name": "Oopbuy Spreadsheet" },
      "inLanguage": "en-US",
      "dateModified": "2026-06-21"
    },
    {
      "@type": "Organization",
      "name": "Oopbuy Spreadsheet Guide",
      "url": SITE_URL,
      "description": "Educational resource providing guides, comparisons, and safety insights about Oopbuy spreadsheet shopping systems."
    }
  ]
};

export default function Home() {
  const [showAllCategories, setShowAllCategories] = useState(false);
  const visibleCategories = showAllCategories ? categories : categories.slice(0, 8);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(homeJsonLd) }}
      />
      <Navbar />

      <main>
        {/* ========== 1. HERO SECTION ========== */}
        <section
          id="home"
          className="relative w-full min-h-[620px] md:min-h-[700px] flex items-center overflow-hidden"
        >
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Elegant%20product%20flat%20lay%20photography%20featuring%20white%20sneakers%20cream%20hoodie%20tan%20leather%20bag%20and%20minimalist%20accessories%20arranged%20harmoniously%20on%20warm%20linen%20fabric%2C%20soft%20natural%20window%20light%2C%20editorial%20fashion%20aesthetic%2C%20warm%20beige%20and%20cream%20tones%2C%20peaceful%20composition%2C%20high%20detail%20textures%2C%20lifestyle%20product%20photography&width=1600&height=900&seq=hero-main-bg-v2&orientation=landscape"
              alt="Oopbuy Spreadsheet fashion product collection displayed on warm linen fabric"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/40"></div>
          </div>

          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="flex-1 text-center lg:text-left">
                <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight drop-shadow-sm">
                  Oopbuy Spreadsheet Guide<br />
                  <span className="text-primary-200">Learn, Compare &amp; Shop Smarter</span>
                </h1>
                <p className="mt-5 text-base md:text-lg text-background-50/85 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                  A complete hub for understanding Oopbuy Spreadsheet systems — tutorials, comparisons, and safety insights all in one structured resource.
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-3 text-background-50/80 text-sm">
                  <span className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-primary-300 text-sm"></i>
                    </div>
                    Beginner-friendly explanations
                  </span>
                  <span className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-primary-300 text-sm"></i>
                    </div>
                    Step-by-step tutorials
                  </span>
                  <span className="flex items-center gap-1.5">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-check-line text-primary-300 text-sm"></i>
                    </div>
                    Real comparison tables
                  </span>
                </div>
                <div className="mt-7 flex flex-col sm:flex-row items-center gap-3">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    Oopbuy Spreadsheet
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-arrow-right-up-line text-sm"></i>
                    </div>
                  </a>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="w-full sm:w-auto px-8 py-3.5 text-sm font-semibold text-foreground-900 bg-background-50 hover:bg-background-100 rounded-full transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    Buy It Now
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-shopping-cart-line text-sm"></i>
                    </div>
                  </a>
                </div>
              </div>

              <div className="flex-1 w-full max-w-lg">
                <div className="bg-background-50/10 backdrop-blur-sm border border-background-50/20 rounded-2xl p-5">
                  <img
                    src="https://readdy.ai/api/search-image?query=Close%20up%20elegant%20product%20photography%20of%20cream%20knit%20sweater%20folded%20beside%20tan%20leather%20accessories%20and%20white%20sneakers%20on%20warm%20wooden%20surface%2C%20minimalist%20fashion%20editorial%2C%20soft%20diffused%20window%20light%2C%20neutral%20beige%20palette%2C%20high%20detail%20textile%20weave%20and%20leather%20grain%2C%20peaceful%20composition&width=800&height=600&seq=hero-card-preview-v2&orientation=landscape"
                    alt="Oopbuy Spreadsheet featured products - sweater sneakers and accessories"
                    className="w-full rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========== 2. TRUST STRIP ========== */}
        <AnimatedSection>
          <section className="w-full bg-background-50 border-b border-background-200/70">
            <div className="w-full px-4 md:px-6 py-6 md:py-8">
              <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-6 md:gap-12">
                {[
                  { img: "https://readdy.ai/api/search-image?query=Small%20clean%20calendar%20icon%20on%20cream%20background%20with%202026%20text%2C%20minimalist%20flat%20lay%2C%20warm%20neutral%20tones%2C%20soft%20shadow&width=200&height=150&seq=trust-updated&orientation=landscape", icon: "ri-calendar-check-line", text: "Updated 2026 Guide" },
                  { img: "https://readdy.ai/api/search-image?query=Small%20shield%20badge%20concept%20on%20beige%20background%2C%20minimalist%20product%20photography%2C%20warm%20lighting%2C%20clean%20composition&width=200&height=150&seq=trust-verified&orientation=landscape", icon: "ri-shield-check-line", text: "Verified Information" },
                  { img: "https://readdy.ai/api/search-image?query=Small%20open%20book%20with%20warm%20pages%20on%20cream%20linen%20background%2C%20minimalist%20photography%2C%20soft%20natural%20light%2C%20peaceful%20learning%20aesthetic&width=200&height=150&seq=trust-beginner&orientation=landscape", icon: "ri-user-heart-line", text: "Beginner Friendly" },
                  { img: "https://readdy.ai/api/search-image?query=Small%20magnifying%20glass%20over%20clean%20text%20on%20cream%20paper%20background%2C%20minimalist%20photography%2C%20warm%20tones%2C%20soft%20lighting%2C%20editorial%20style&width=200&height=150&seq=trust-seo&orientation=landscape", icon: "ri-search-line", text: "SEO Optimized Content" },
                ].map((item) => (
                  <div key={item.text} className="flex items-center gap-3 text-foreground-600">
                    <div className="w-14 h-10 rounded-lg overflow-hidden shrink-0">
                      <img
                        src={item.img}
                        alt={item.text}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${item.icon} text-sm text-primary-500`}></i>
                      </div>
                      <span className="text-sm font-medium whitespace-nowrap">{item.text}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* ========== 3. WHAT IS SECTION ========== */}
        <AnimatedSection>
          <section id="what-is" className="w-full bg-background-50">
            <div className="w-full px-4 md:px-6 py-16 md:py-20">
              <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                <div className="flex-1 w-full max-w-lg">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20overhead%20flat%20lay%20of%20organized%20shopping%20items%20including%20folded%20cream%20clothing%20tan%20accessories%20and%20neatly%20arranged%20product%20cards%20on%20warm%20wooden%20desk%2C%20minimalist%20editorial%20photography%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20high%20detail%2C%20peaceful%20organized%20composition&width=800&height=600&seq=what-is-organized-v2&orientation=landscape"
                    alt="Organized Oopbuy Spreadsheet shopping system workflow"
                    className="w-full rounded-2xl"
                  />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 leading-tight">
                    What Is Oopbuy Spreadsheet?
                  </h2>
                  <p className="mt-5 text-base text-foreground-600 leading-relaxed max-w-xl">
                    The Oopbuy spreadsheet is a structured shopping system designed to make agent-based ordering faster, more organized, and significantly less error-prone. Instead of browsing scattered product pages and manually copying links, you use a single spreadsheet template that brings together product listings, prices, size options, and supplier information in one clean view.
                  </p>
                  <ul className="mt-5 space-y-3">
                    {[
                      "Structured shopping system with organized product listings",
                      "Faster agent-based ordering with reduced manual entry",
                      "Built-in comparison tools for prices and suppliers",
                      "Error reduction through automated data validation",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                          <i className="ri-checkbox-circle-line text-primary-500 text-sm"></i>
                        </div>
                        <span className="text-sm text-foreground-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/oopbuy-spreadsheet-guide"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-background-50 bg-foreground-900 hover:bg-foreground-800 rounded-full transition-all whitespace-nowrap cursor-pointer"
                  >
                    Read Full Guide
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* ========== 4. CATEGORIES SECTION ========== */}
        <AnimatedSection>
          <section className="w-full bg-background-100">
            <div className="w-full px-4 md:px-6 py-16 md:py-20">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                      Browse Product Categories
                    </h2>
                    <p className="mt-2 text-sm text-foreground-500">
                      Explore all Oopbuy spreadsheet shopping categories with direct links to the main store
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAllCategories(!showAllCategories)}
                    className="px-5 py-2 text-sm font-medium text-background-50 bg-foreground-900 hover:bg-foreground-800 rounded-full transition-all whitespace-nowrap cursor-pointer"
                  >
                    {showAllCategories ? "Show Less" : "All Categories"}
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  {visibleCategories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="group bg-background-50 border border-background-200/70 hover:border-primary-300 rounded-xl p-4 flex flex-col items-center gap-3 transition-all cursor-pointer hover:bg-primary-50/50"
                    >
                      <div className="w-11 h-11 flex items-center justify-center bg-background-100 group-hover:bg-primary-100 rounded-xl transition-colors">
                        <i className={`${cat.icon} text-xl text-primary-500`}></i>
                      </div>
                      <span className="text-xs font-medium text-foreground-700 text-center leading-tight whitespace-nowrap">
                        {cat.name}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* ========== 5. FEATURES SECTION ========== */}
        <AnimatedSection>
          <section className="w-full bg-background-50">
            <div className="w-full px-4 md:px-6 py-16 md:py-20">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    What You Can Do With Oopbuy Spreadsheet
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500 max-w-xl mx-auto">
                    Everything you need for organized, efficient spreadsheet-based shopping
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {features.map((feat) => (
                    <div
                      key={feat.title}
                      className="bg-background-50 border border-background-200/70 rounded-xl p-6 hover:border-primary-200 transition-all group cursor-default"
                    >
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-lg mb-4">
                        <i className={`${feat.icon} text-lg text-primary-500`}></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">{feat.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{feat.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* ========== 6. COMPARISON SECTION ========== */}
        <AnimatedSection>
          <section id="comparison" className="w-full bg-background-100">
            <div className="w-full px-4 md:px-6 py-16 md:py-20">
              <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
                <div className="flex-1 w-full">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                    Oopbuy Spreadsheet vs Traditional Shopping
                  </h2>
                  <p className="text-sm text-foreground-500 mb-7">
                    See why spreadsheet-based shopping outperforms the old way
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-background-300">
                          <th className="text-left py-3 pr-4 font-semibold text-foreground-700">Feature</th>
                          <th className="text-left py-3 pr-4 font-semibold text-primary-600">
                            <div className="w-5 h-5 flex items-center justify-center inline">
                              <i className="ri-table-line text-sm"></i>
                            </div>{" "}
                            Oopbuy Spreadsheet
                          </th>
                          <th className="text-left py-3 font-semibold text-foreground-400">Traditional</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparisonData.map((row) => (
                          <tr key={row.feature} className="border-b border-background-200/70">
                            <td className="py-3.5 pr-4 font-medium text-foreground-800">{row.feature}</td>
                            <td className="py-3.5 pr-4 text-foreground-700">{row.oopbuy}</td>
                            <td className="py-3.5 text-foreground-400">{row.traditional}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="lg:w-80 shrink-0 bg-background-50 border border-background-200/70 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-3">Why Spreadsheet Wins</h3>
                  <ul className="space-y-3">
                    {[
                      "Organized data reduces shopping time by up to 60%",
                      "Batch processing handles multiple items in one go",
                      "Built-in validation catches errors before ordering",
                      "Single view replaces dozens of browser tabs",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2.5">
                        <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                          <i className="ri-check-line text-accent-500 text-xs"></i>
                        </div>
                        <span className="text-sm text-foreground-600 leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/oopbuy-spreadsheet-guide"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                  >
                    View Full Comparison
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* ========== 7. HOW IT WORKS SECTION ========== */}
        <AnimatedSection>
          <section id="how-it-works" className="w-full bg-background-50">
            <div className="w-full px-4 md:px-6 py-16 md:py-20">
              <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                    How Oopbuy Spreadsheet Works
                  </h2>
                  <p className="mt-2 text-sm text-foreground-500 max-w-lg mx-auto">
                    Five simple steps from opening your spreadsheet to tracking your order
                  </p>
                </div>
                <div className="relative">
                  <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-background-200"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                    {steps.map((s) => (
                      <div key={s.step} className="relative flex flex-col items-center text-center group">
                        <div className="w-16 h-16 flex items-center justify-center bg-primary-500 text-background-50 rounded-full text-xl font-bold mb-4 relative z-10 shrink-0">
                          {s.step}
                        </div>
                        <h3 className="text-base font-semibold text-foreground-900 mb-2">{s.title}</h3>
                        <p className="text-sm text-foreground-500 leading-relaxed">{s.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* ========== 8. SAFETY SECTION ========== */}
        <AnimatedSection>
          <section id="safety" className="w-full bg-background-100">
            <div className="w-full px-4 md:px-6 py-16 md:py-20">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  Is Oopbuy Spreadsheet Safe?
                </h2>
                <p className="text-sm text-foreground-500 mb-10 max-w-lg">
                  Important safety considerations when using spreadsheet-based shopping systems
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 flex items-center justify-center bg-primary-50 rounded-lg">
                        <i className="ri-shield-check-line text-xl text-primary-500"></i>
                      </div>
                      <h3 className="text-lg font-semibold text-foreground-900">Safety Best Practices</h3>
                    </div>
                    <ul className="space-y-4">
                      {[
                        { title: "Check Sources Carefully", desc: "Always verify the supplier and agent before sending your spreadsheet with order details." },
                        { title: "Avoid Fake Listings", desc: "Cross-reference product URLs with the official store to ensure listing authenticity." },
                        { title: "Use Trusted Agents", desc: "Work only with agents who have verified track records and positive community feedback." },
                        { title: "Review Before Submitting", desc: "Double-check all prices, sizes, and quantities in your spreadsheet before placing the order." },
                      ].map((item) => (
                        <li key={item.title} className="flex items-start gap-3">
                          <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                            <i className="ri-checkbox-circle-line text-primary-500 text-sm"></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground-800">{item.title}</p>
                            <p className="text-sm text-foreground-500 mt-0.5">{item.desc}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex flex-col gap-6">
                    <div className="bg-accent-50 border border-accent-200 rounded-xl p-6 md:p-8 flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg">
                          <i className="ri-error-warning-line text-xl text-accent-600"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-foreground-900">Important Notice</h3>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                        Spreadsheet-based shopping systems are powerful tools, but they require responsible use. Always verify product authenticity, check agent credentials, and understand the platform&apos;s terms before committing to any purchase.
                      </p>
                      <p className="text-sm text-foreground-500 leading-relaxed">
                        This guide site provides educational content about Oopbuy spreadsheet systems. We recommend reading the full safety guide before using any spreadsheet-based ordering workflow.
                      </p>
                    </div>
                    <Link
                      to="/oopbuy-spreadsheet-guide"
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className="self-start inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-foreground-900 hover:bg-foreground-800 rounded-full transition-all whitespace-nowrap cursor-pointer"
                    >
                      Read Safety Guide
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-shield-check-line text-xs"></i>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* ========== 9. CONTENT HUB ========== */}
        <AnimatedSection>
          <section id="content-hub" className="w-full bg-background-50">
            <div className="w-full px-4 md:px-6 py-16 md:py-20">
              <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                      Latest Guides &amp; Articles
                    </h2>
                    <p className="mt-2 text-sm text-foreground-500">
                      In-depth Oopbuy spreadsheet tutorials, comparisons, and tips — {articles.length} guides available
                    </p>
                  </div>
                  <Link
                    to="/oopbuy-spreadsheet-guide"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="px-5 py-2 text-sm font-medium text-primary-600 border border-primary-300 hover:bg-primary-50 rounded-full transition-all whitespace-nowrap cursor-pointer"
                  >
                    View All Articles
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                  {articles.map((article) => (
                    <Link
                      key={article.title}
                      to={article.url}
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                      className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden hover:border-primary-200 transition-all cursor-pointer flex flex-col"
                    >
                      <div className="aspect-[8/5] overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4 flex flex-col flex-1">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug mb-2 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 flex-1">
                          {article.excerpt}
                        </p>
                        <div className="mt-3 pt-3 border-t border-background-100 flex items-center gap-1.5 text-xs font-medium text-primary-500">
                          Read Article
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-arrow-right-line text-[10px]"></i>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>

        {/* ========== 10. FINAL CTA ========== */}
        <AnimatedSection>
          <section className="relative w-full py-20 md:py-28 overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Wide%20panoramic%20lifestyle%20product%20photography%20featuring%20elegant%20cream%20hoodie%20white%20sneakers%20tan%20leather%20bag%20and%20folded%20beige%20linen%20clothing%20on%20warm%20wooden%20floor%20by%20sunlit%20window%2C%20minimalist%20editorial%20aesthetic%2C%20soft%20natural%20light%2C%20warm%20neutral%20tones%2C%20peaceful%20morning%20vibe%2C%20high%20detail%20composition&width=1600&height=800&seq=cta-final-bg-v2&orientation=landscape"
                alt="Oopbuy Spreadsheet product collection lifestyle scene"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-foreground-900/75 via-foreground-900/55 to-foreground-900/35"></div>
            </div>
            <div className="relative z-10 w-full px-4 md:px-6">
              <div className="max-w-7xl mx-auto">
                <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight max-w-2xl">
                  Start Using Oopbuy Spreadsheet the Smart Way
                </h2>
                <p className="mt-4 text-base text-background-50/70 max-w-lg leading-relaxed">
                  Everything you need is here — guides, comparisons, and safety insights in one structured system. Ready to shop smarter?
                </p>
                <div className="mt-8 flex flex-col sm:flex-row items-start gap-3">
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-8 py-3.5 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-rocket-line text-sm"></i>
                    </div>
                    Explore Full Guides
                  </a>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-8 py-3.5 text-sm font-semibold text-foreground-900 bg-background-50 hover:bg-background-100 rounded-full transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-shopping-cart-line text-sm"></i>
                    </div>
                    Buy It Now
                  </a>
                </div>
              </div>
            </div>
          </section>
        </AnimatedSection>
      </main>

      <Footer />
    </>
  );
}